import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";

const MAX_HISTORY = 60;

export function useBoard(projectId) {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("saved");
  const [hist, setHist] = useState({ past: 0, future: 0 });
  const timer = useRef();
  const latest = useRef(null);
  const past = useRef([]);
  const future = useRef([]);

  const load = useCallback(async () => {
    setError("");
    try { const { data } = await api.get(`/projects/${projectId}/board`); setBoard(data); latest.current = data; }
    catch (e) { setError(errMsg(e)); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const persist = useCallback(async (b) => {
    setStatus("saving");
    try { await api.put(`/boards/${b.id}`, { rooms: b.rooms, elements: b.elements, connections: b.connections, sketches: b.sketches || [] }); setStatus("saved"); }
    catch (e) { setStatus("error"); console.error(errMsg(e)); }
  }, []);

  const schedule = useCallback(() => {
    setStatus("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(latest.current), 700);
  }, [persist]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    if (!latest.current || status === "saved") return;
    await persist(latest.current);
  }, [status, persist]);

  const update = useCallback((fn, save = true, historyBase = null) => {
    const prev = latest.current;
    const next = fn(prev);
    if (save && prev) {
      past.current = [...past.current.slice(-MAX_HISTORY + 1), historyBase || prev];
      future.current = [];
      setHist({ past: past.current.length, future: 0 });
    }
    latest.current = next;
    setBoard(next);
    if (save) schedule();
  }, [schedule]);

  const step = useCallback((from, to) => {
    if (!from.current.length) return;
    const target = from.current.pop();
    to.current.push(latest.current);
    latest.current = { ...target, id: latest.current.id, share_token: latest.current.share_token };
    setBoard(latest.current);
    setHist({ past: past.current.length, future: future.current.length });
    schedule();
  }, [schedule]);

  useEffect(() => () => clearTimeout(timer.current), []);
  return { board, setBoard: (b) => { setBoard(b); latest.current = b; }, update, status, error, load, flush,
    undo: () => step(past, future), redo: () => step(future, past), canUndo: hist.past > 0, canRedo: hist.future > 0 };
}
